import { useEffect, useMemo, useState } from 'react';
import { CheckCircle, Package, Phone, Truck, X, XCircle } from 'lucide-react';
import { api } from '../../lib/api';

interface OrdersViewProps {
  restaurantId: string;
  compact?: boolean;
  staffView?: boolean;
  showTitle?: boolean;
  currencySymbol?: string;
  currencyDecimals?: number;
}

type UiOrderStatus =
  | 'PENDING'
  | 'CONFIRMED'
  | 'PREPARING'
  | 'READY'
  | 'OUT_FOR_DELIVERY'
  | 'DELIVERED'
  | 'COMPLETED'
  | 'CANCELLED';

interface UiOrderItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  subtotal: number;
  notes?: string | null;
}

interface UiOrder {
  id: string;
  orderNumber: string;
  customerName: string;
  customerPhone: string;
  deliveryAddress: string;
  createdAt: string;
  subtotal: number;
  deliveryFee: number;
  discount: number;
  tax: number;
  total: number;
  status: UiOrderStatus;
  items: UiOrderItem[];
}

const normalizeStatus = (value: unknown): UiOrderStatus => {
  const upper = String(value || '').toUpperCase();
  if (
    [
      'PENDING',
      'CONFIRMED',
      'PREPARING',
      'READY',
      'OUT_FOR_DELIVERY',
      'DELIVERED',
      'COMPLETED',
      'CANCELLED',
    ].includes(upper)
  ) {
    return upper as UiOrderStatus;
  }
  return 'PENDING';
};

const statusLabel = (status: UiOrderStatus) =>
  status
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const statusPillClasses = (status: UiOrderStatus) => {
  switch (status) {
    case 'PENDING':
      return 'bg-orange-100 text-orange-800 border border-orange-200';
    case 'CONFIRMED':
      return 'bg-blue-100 text-blue-800 border border-blue-200';
    case 'PREPARING':
      return 'bg-purple-100 text-purple-800 border border-purple-200';
    case 'READY':
      return 'bg-emerald-100 text-emerald-800 border border-emerald-200';
    case 'OUT_FOR_DELIVERY':
      return 'bg-indigo-100 text-indigo-800 border border-indigo-200';
    case 'DELIVERED':
      return 'bg-green-100 text-green-800 border border-green-200';
    case 'COMPLETED':
      return 'bg-gray-100 text-gray-800 border border-gray-200';
    case 'CANCELLED':
      return 'bg-red-100 text-red-800 border border-red-200';
    default:
      return 'bg-gray-100 text-gray-800 border border-gray-200';
  }
};

export function OrdersView({
  restaurantId,
  compact = false,
  showTitle = true,
  currencySymbol = '₹',
  currencyDecimals = 2,
}: OrdersViewProps) {
  const [orders, setOrders] = useState<UiOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<'ALL' | UiOrderStatus>('ALL');
  const [confirmingOrder, setConfirmingOrder] = useState<UiOrder | null>(null);
  const [cancellingOrder, setCancellingOrder] = useState<UiOrder | null>(null);
  const resolvedDecimals = Math.max(
    2,
    Number.isFinite(Number(currencyDecimals)) ? Number(currencyDecimals) : 2
  );
  const formatMoney = (value: number | string) => {
    const amount = Number(value ?? 0);
    const safe = Number.isFinite(amount) ? amount : 0;
    return `${currencySymbol}${safe.toLocaleString('en-IN', {
      minimumFractionDigits: resolvedDecimals,
      maximumFractionDigits: resolvedDecimals,
    })}`;
  };

  const loadOrders = async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const response = await api.orders.getAll({ restaurantId, limit: 100 } as any);
      const raw = Array.isArray((response as any)?.data)
        ? (response as any).data
        : Array.isArray((response as any)?.data?.orders)
        ? (response as any).data.orders
        : [];

      const mapped: UiOrder[] = raw.map((order: any) => ({
        id: String(order?.id || ''),
        orderNumber: String(order?.orderNumber || order?.id || ''),
        customerName: String(order?.customer?.name || order?.customerName || 'Customer'),
        customerPhone: String(order?.customer?.phone || order?.customerPhone || 'N/A'),
        deliveryAddress: String(order?.deliveryAddress || order?.customerAddress || 'N/A'),
        createdAt: String(order?.createdAt || order?.placedAt || new Date().toISOString()),
        subtotal: Number(order?.subtotal || 0),
        deliveryFee: Number(order?.deliveryFee || 0),
        discount: Number(order?.discount || 0),
        tax: Number(order?.tax || 0),
        total: Number(order?.total || 0),
        status: normalizeStatus(order?.status),
        items: Array.isArray(order?.items)
          ? order.items.map((item: any) => ({
              id: String(item?.id || ''),
              name: String(item?.menuItem?.name || item?.name || 'Item'),
              price: Number(item?.price || 0),
              quantity: Number(item?.quantity || 0),
              subtotal: Number(item?.subtotal || 0),
              notes: item?.notes || null,
            }))
          : [],
      }));

      setOrders(mapped);
    } catch {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadOrders();
  }, [restaurantId]);

  const updateOrderStatus = async (orderId: string, status: UiOrderStatus) => {
    try {
      await api.orders.updateStatus(orderId, status);
      await loadOrders();
    } catch (error: any) {
      alert(error?.message || 'Failed to update order status');
    }
  };

  const nextStatus = (status: UiOrderStatus): UiOrderStatus | null => {
    switch (status) {
      case 'PENDING':
        return 'CONFIRMED';
      case 'READY':
        return 'OUT_FOR_DELIVERY';
      case 'OUT_FOR_DELIVERY':
        return 'DELIVERED';
      default:
        return null;
    }
  };

  const actionLabel = (status: UiOrderStatus) => {
    switch (status) {
      case 'PENDING':
        return 'Confirm Order';
      case 'READY':
        return 'Send for Delivery';
      case 'OUT_FOR_DELIVERY':
        return 'Mark Delivered';
      default:
        return null;
    }
  };

  const orderTabs = useMemo(
    () => [
      { key: 'ALL' as const, label: 'All Orders', count: orders.length },
      { key: 'PENDING' as const, label: 'Pending', count: orders.filter((order) => order.status === 'PENDING').length },
      { key: 'CONFIRMED' as const, label: 'Confirmed', count: orders.filter((order) => order.status === 'CONFIRMED').length },
      { key: 'PREPARING' as const, label: 'Preparing', count: orders.filter((order) => order.status === 'PREPARING').length },
      { key: 'READY' as const, label: 'Ready', count: orders.filter((order) => order.status === 'READY').length },
      {
        key: 'OUT_FOR_DELIVERY' as const,
        label: 'Out for Delivery',
        count: orders.filter((order) => order.status === 'OUT_FOR_DELIVERY').length,
      },
      { key: 'DELIVERED' as const, label: 'Delivered', count: orders.filter((order) => order.status === 'DELIVERED').length },
    ],
    [orders]
  );

  const filtered = useMemo(() => {
    if (selectedStatus === 'ALL') {
      return orders;
    }

    return orders.filter((order) => order.status === selectedStatus);
  }, [orders, selectedStatus]);

  if (compact) {
    return (
      <div className="space-y-3">
        {filtered.slice(0, 5).map((order) => (
          <div key={order.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div>
              <p className="text-gray-900">{order.orderNumber}</p>
              <p className="text-gray-600 text-sm">{order.customerName}</p>
            </div>
            <div className="text-right">
              <p className="text-gray-900">{formatMoney(order.total)}</p>
              <span className="text-xs text-gray-500">{statusLabel(order.status)}</span>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        {showTitle ? <h1 className="text-gray-900">Order Management</h1> : <div />}
        {loading && <span className="text-sm text-gray-500">Loading...</span>}
      </div>

      <div className="mb-6 rounded-[28px] border border-gray-100 bg-white p-3 shadow-sm">
        <div className="flex flex-wrap gap-3">
          {orderTabs.map((tab) => {
            const isActive = selectedStatus === tab.key;

            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setSelectedStatus(tab.key)}
                className={[
                  'rounded-2xl px-5 py-3 text-sm font-semibold transition-all duration-200',
                  isActive
                    ? 'bg-blue-600 text-white shadow-[0_10px_24px_rgba(37,99,235,0.28)]'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
                ].join(' ')}
              >
                {tab.label} ({tab.count})
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {!filtered.length ? (
          <div className="col-span-2 bg-white rounded-xl shadow-sm p-12 text-center">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">
              {selectedStatus === 'ALL' ? 'No orders found' : `No ${statusLabel(selectedStatus)} orders found`}
            </p>
          </div>
        ) : (
          filtered.map((order) => (
            <div key={order.id} className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-gray-900 mb-1">{order.orderNumber}</h3>
                  <p className="text-gray-500 text-sm">{new Date(order.createdAt).toLocaleString()}</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${statusPillClasses(order.status)}`}>
                  {statusLabel(order.status)}
                </span>
              </div>

              <div className="space-y-1 mb-4 pb-4 border-b border-gray-200">
                <p className="text-sm text-gray-700">{order.customerName}</p>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Phone className="w-4 h-4" />
                  <span>{order.customerPhone}</span>
                </div>
                <p className="text-xs text-gray-500">{order.deliveryAddress}</p>
              </div>

              <div className="space-y-2 mb-4">
                {order.items.map((item) => (
                  <div key={item.id} className="flex justify-between text-sm">
                    <span className="text-gray-700">
                      {item.quantity}x {item.name}
                    </span>
                    <span className="text-gray-900">{formatMoney(item.subtotal || item.price * item.quantity)}</span>
                  </div>
                ))}
              </div>

              <div className="pt-3 border-t border-gray-200 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span>{formatMoney(order.subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Delivery Fee</span>
                  <span>{order.deliveryFee === 0 ? 'FREE' : formatMoney(order.deliveryFee)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Tax</span>
                  <span>{formatMoney(order.tax)}</span>
                </div>
                {order.discount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Discount</span>
                    <span>-{formatMoney(order.discount)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 mt-2 border-t border-gray-200">
                  <span className="text-gray-900">Total</span>
                  <span className="text-gray-900">{formatMoney(order.total)}</span>
                </div>
              </div>

              {order.status !== 'DELIVERED' && order.status !== 'COMPLETED' && order.status !== 'CANCELLED' && (
                <div className="flex gap-2">
                  {nextStatus(order.status) && (
                    <button
                      onClick={() => {
                        const next = nextStatus(order.status);
                        if (!next) return;
                        if (order.status === 'PENDING') {
                          setConfirmingOrder(order);
                          return;
                        }
                        void updateOrderStatus(order.id, next);
                      }}
                      className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                    >
                      {order.status === 'PENDING' && <CheckCircle className="w-4 h-4" />}
                      {order.status === 'READY' && <Truck className="w-4 h-4" />}
                      {order.status === 'OUT_FOR_DELIVERY' && <Package className="w-4 h-4" />}
                      {actionLabel(order.status) || statusLabel(nextStatus(order.status)!)}
                    </button>
                  )}
                  {order.status === 'PENDING' && (
                    <button
                      onClick={() => setCancellingOrder(order)}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                    >
                      <XCircle className="w-4 h-4" />
                      Cancel
                    </button>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Confirm Order Popup */}
      {confirmingOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <button
              type="button"
              onClick={() => setConfirmingOrder(null)}
              className="absolute right-4 top-4 rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>

            <h2 className="text-gray-900">Confirm Order</h2>
            <p className="mt-2 text-sm text-gray-600">
              Are you sure you want to confirm this order{' '}
              <span className="font-semibold text-gray-900">{confirmingOrder.orderNumber}</span>?
            </p>

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => setConfirmingOrder(null)}
                className="flex-1 rounded-lg bg-red-600 py-2 font-semibold text-white hover:bg-red-700 transition-colors"
              >
                Close
              </button>
              <button
                type="button"
                onClick={async () => {
                  const orderId = confirmingOrder.id;
                  await updateOrderStatus(orderId, 'CONFIRMED');
                  setConfirmingOrder(null);
                }}
                className="flex-1 rounded-lg bg-blue-600 py-2 font-semibold text-white hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                <CheckCircle className="w-4 h-4" />
                Confirm Order
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Order Popup */}
      {cancellingOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <button
              type="button"
              onClick={() => setCancellingOrder(null)}
              className="absolute right-4 top-4 rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>

            <h2 className="text-gray-900">Cancel Order</h2>
            <p className="mt-2 text-sm text-gray-600">
              Are you sure you want to cancel this order{' '}
              <span className="font-semibold text-gray-900">{cancellingOrder.orderNumber}</span>?
            </p>

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => setCancellingOrder(null)}
                className="flex-1 rounded-lg bg-gray-100 py-2 font-semibold text-gray-700 hover:bg-gray-200 transition-colors"
              >
                Keep Order
              </button>
              <button
                type="button"
                onClick={async () => {
                  const orderId = cancellingOrder.id;
                  await updateOrderStatus(orderId, 'CANCELLED');
                  setCancellingOrder(null);
                }}
                className="flex-1 rounded-lg bg-red-600 py-2 font-semibold text-white hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
              >
                <XCircle className="w-4 h-4" />
                Cancel Order
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
